// review-report-promotion.ts
//
// P20 — closes "0 caller" #12. `writeWorkerReviewReportToStaging`
// (review-local-adapter.ts) existed with ZERO production callers, and the
// reason it never got one is a boundary, not an oversight:
//
//   - The worker writes only inside its own attempt staging dir
//     (`<repoRoot>/.avatar/attempt-output/<run_id>/`) — that is the whole
//     surface an adapter's `Write(.avatar/attempt-output/**)` grants.
//   - The file `run-command-release-flow-wiring.ts` reads is
//     `<controlRoot>/review-staging/<candidateSha>.json` — OUTSIDE the
//     worktree, and deliberately so: review-state is controller-only
//     authority (§9.3), and a worker that could write there could stage its
//     own clean review.
//
// So the report has to CROSS that boundary, and only the controller may
// carry it. This module is that carry step: read the report at a FIXED
// convention path inside this attempt's staging dir, and hand it to
// `writeWorkerReviewReportToStaging`.
//
// Why a convention path and NOT `evidence[].report_path`: `evidence[]` is
// not an artifact manifest. result-verifier-evidence.ts treats each entry as
// a claim "I ran approved recipe X", re-runs that recipe, and rejects the
// attempt when the recipe is not in the mission's verification-profile
// (`checkRecipeIsApproved`). Routing the report through `evidence[]` would
// therefore force a fake "review-bootstrap" recipe into every profile just to
// carry a file path — measured: the attempt is rejected with `recipe
// "review-bootstrap" không có trong verification-profile đã duyệt`. The
// staging dir is already derived by the controller alone
// (supervisor-attempt-cycle.ts builds `<repoRoot>/.avatar/attempt-output/
// <run_id>`), so the path needs nothing from the worker but the file itself.
//
// Fail direction is deliberate: every rejection returns `no_report` and
// writes NOTHING. An absent staging file makes the release flow answer
// `waiting_review_report` — it never fabricates a risk value (see
// run-command-release-flow-wiring.ts). Writing a placeholder on a malformed
// report would be the opposite, and far worse: a receipt derived from a
// report nobody produced.
import { promises as fs } from "node:fs";
import { dirname, join, resolve } from "node:path";
import { ensureDir } from "../filesystem-helpers.js";
import { canonicalJson } from "./canonical-json-and-checksum.js";
import {
  type WorkerReviewReport,
  writeWorkerReviewReportToStaging,
} from "./review-local-adapter.js";

export interface PromoteWorkerReviewReportInput {
  controlRoot: string;
  repoRoot: string;
  /** The attempt whose staging dir is the ONLY accepted source for the report. */
  runId: string;
  /** SHA the release flow will look the report up by — `route.commitHeadSha`. */
  candidateSha: string;
  /**
   * P21: the report as declared in the worker's RESULT (`review_report`).
   * Preferred over the staging file when present, because this is the copy
   * the strict schema validated (autopilot-result-schema.ts) and the
   * verifier gated on (result-verifier.ts step 6c) — a hand-written staging
   * file has passed through neither. The file route is kept as a fallback:
   * it is the one path that has actually run end to end (P20's real
   * mission), so a worker that writes the file but omits the field is still
   * carried across rather than silently dropped.
   */
  reportFromResult?: unknown;
  /**
   * H1 nửa B: adapter đã sinh ra report này. Quyết định TÊN FILE trong
   * `review-staging/`, vì path cũ khoá theo SHA nên hai reviewer trên cùng
   * candidate sẽ đè nhau — mất đúng verdict mà phase này tồn tại để cứu.
   *
   * Không khai, hoặc khai `review-local-v1` ⇒ giữ NGUYÊN path cũ
   * `<sha>.json`: e2e
   * (`autopilot-release-run-resumes-flow-after-ticket-loop-real-cli.e2e.test.ts:128`) và
   * `review-report-promotion.test.ts` đều assert đúng path đó, và mọi mission
   * đã chạy trước thay đổi này có report nằm ở đấy.
   */
  adapterId?: string;
}

/** Adapter có report nằm ở path lịch sử `<sha>.json` (xem `adapterId`). */
const LEGACY_STAGING_ADAPTER_ID = "review-local-v1";

/** `<sha>.json` cho reviewer gốc, `<sha>.<adapter>.json` cho mọi reviewer thêm sau. */
export function reviewStagingFileName(candidateSha: string, adapterId?: string): string {
  return adapterId === undefined || adapterId === LEGACY_STAGING_ADAPTER_ID
    ? `${candidateSha}.json`
    : `${candidateSha}.${adapterId}.json`;
}

/** File name the review playbook (workflows/autopilot/review.md) tells the session to write. */
export const REVIEW_REPORT_FILE_NAME = "review-report.json";

export type PromoteWorkerReviewReportResult =
  | {
      status: "promoted";
      stagedPath: string;
      sourcePath: string;
      /**
       * True when BOTH sources were present and disagreed. The result still
       * wins (it is the copy the schema validated and the verifier gated
       * on), but the disagreement is reported rather than dropped: two
       * copies the same worker wrote that do not match is a signal, and
       * this project has repeatedly paid for branches that stay silent
       * (P20's Lỗi A mapped a real `gate_blocked` onto a bland
       * `dispatched`, and the operator saw `idle`). False when the sources
       * agree, when only one exists, or when the staging file is
       * unreadable — none of those is a disagreement.
       */
      sourceMismatch: boolean;
    }
  | { status: "no_report"; reason: string };

/** `<repoRoot>/.avatar/attempt-output/<runId>` — mirrors supervisor-attempt-cycle.ts's own stagingDir. */
function attemptStagingDir(repoRoot: string, runId: string): string {
  return resolve(repoRoot, ".avatar", "attempt-output", runId);
}

function isReportShaped(value: unknown): value is WorkerReviewReport {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Controller-side promotion. Called from supervisor-loop.ts once a
 * `review-local-v1` ticket's attempt is committed, with the commit's own head
 * SHA as `candidateSha` — the SAME SHA `readWorkerReviewReport` derives from
 * `git rev-parse HEAD` when the release flow later looks the report up.
 */
export async function promoteWorkerReviewReportToControlRoot(
  input: PromoteWorkerReviewReportInput,
): Promise<PromoteWorkerReviewReportResult> {
  const stagingPath = join(attemptStagingDir(input.repoRoot, input.runId), REVIEW_REPORT_FILE_NAME);
  const hasResultReport = input.reportFromResult !== undefined;
  const sourcePath = hasResultReport ? "result.review_report" : stagingPath;

  let parsed: unknown;
  if (hasResultReport) {
    parsed = input.reportFromResult;
  } else {
    try {
      parsed = JSON.parse(await fs.readFile(stagingPath, "utf8"));
    } catch {
      return {
        status: "no_report",
        reason: `Không đọc được report JSON tại "${stagingPath}".`,
      };
    }
  }
  if (!isReportShaped(parsed)) {
    return {
      status: "no_report",
      reason: `Report tại "${sourcePath}" không phải object — không promote.`,
    };
  }

  // Only meaningful when the result carried a report: read the staging
  // file too, purely to report whether the two copies agree. A missing or
  // unreadable file is NOT a disagreement — it is the normal shape once
  // the result is the carrier.
  let sourceMismatch = false;
  if (hasResultReport) {
    try {
      const onDisk = JSON.parse(await fs.readFile(stagingPath, "utf8"));
      sourceMismatch = canonicalJson(onDisk) !== canonicalJson(parsed);
    } catch {
      sourceMismatch = false;
    }
  }

  const stagedPath = join(
    input.controlRoot,
    "review-staging",
    reviewStagingFileName(input.candidateSha, input.adapterId),
  );
  // `writeFileDurable` (via writeWorkerReviewReportToStaging) writes its tmp
  // file next to the target and never creates directories itself — same as
  // review-state-store.ts's own writeReviewReceipt, which ensures the dir
  // first for exactly this reason.
  await ensureDir(dirname(stagedPath));
  await writeWorkerReviewReportToStaging(stagedPath, parsed);
  return { status: "promoted", stagedPath, sourcePath, sourceMismatch };
}
